import { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router';

import { useNavStore } from '@shared/app-shell/nav-state';
import { useSessionStore } from '@shared/auth/session';
import { cn } from '@shared/ui/cn';
import { CloseIcon } from '@shared/ui/icons';

import { chromeCopy } from './copy';
import { navEntryState, visibleNavGroups } from './nav';

/**
 * One list in the document, two containers.
 *
 * Through the tablet tier it is a **bottom sheet** — the one mobile overlay the
 * design system specifies — opened from the top bar's handle, focus-trapped,
 * `Esc`-dismissible, scrim-backed, returning focus to the handle. From
 * `--bp-lg` the same list is a permanent 232 px labelled column: no fixed
 * positioning, no scrim, no handle, and nothing to open or close.
 *
 * Neither the design system's four-item bottom bar nor its collapsed icon rail
 * survives contact with this area, and both departures are measurements rather
 * than preferences: four of seventeen destinations would be a configuration the
 * catalog does not declare, and a seventeen-item icon rail needs `overflow-y`,
 * which clips every tooltip the rail depends on.
 *
 * **The list is the account's, not the console's.** Seventeen is the maximum;
 * what draws is what this account holds the role for in the service that owns
 * it, and a group that loses all its entries loses its caption with them. This
 * is presentation — the route guard redirects and the backend refuses — and a
 * service whose capability read failed stays visible rather than disappearing on
 * a network blip.
 *
 * `aria-current` has two values. `page` marks the exact route; a section
 * ancestor stays lit for a sub-route without claiming to be it.
 */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

export function SideNav({ id, toggleId }: { id: string; toggleId: string }) {
  const open = useNavStore((state) => state.open);
  const setOpen = useNavStore((state) => state.setOpen);
  // Subscribed rather than read once: the map lands after the bootstrap's
  // `/me/permissions` sweep resolves, which is after the first paint.
  const capabilities = useSessionStore((state) => state.capabilities);
  const groups = visibleNavGroups(capabilities);
  const { pathname } = useLocation();
  const sheetRef = useRef<HTMLElement>(null);

  function close() {
    setOpen(false);
    document.getElementById(toggleId)?.focus();
  }

  useEffect(() => {
    if (!open) return;

    // The sheet suspends the page, so it traps focus while it is up — a drawer
    // is not a `<dialog>` here, because from `--bp-lg` it becomes content in
    // the document flow, and what is a `<dialog>` does not stop being one when
    // the viewport changes.
    const sheet = sheetRef.current;
    sheet?.querySelector<HTMLElement>(FOCUSABLE)?.focus();

    function onKeyDown(event: KeyboardEvent) {
      const root = sheetRef.current;
      if (!root) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
        document.getElementById(toggleId)?.focus();
        return;
      }

      if (event.key !== 'Tab') return;

      const items = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (element) => element.offsetParent !== null,
      );
      if (items.length === 0) return;

      const first = items[0] as HTMLElement;
      const last = items[items.length - 1] as HTMLElement;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, setOpen, toggleId]);

  return (
    <aside
      ref={sheetRef}
      id={id}
      aria-label={chromeCopy.nav.label}
      className={cn(
        // Tablet tier and below: a sheet over a scrim, rendered as a sibling of
        // the chrome so no `backdrop-filter` ancestor re-scopes its position.
        'fixed z-[70] overflow-y-auto border-t border-border bg-surface-overlay px-gutter py-5 shadow-4 transition-transform duration-slow ease-decel',
        'rounded-t-xl [inset-block-end:0] [inset-inline:0] [max-block-size:82vh]',
        open ? '[transform:translateY(0)]' : 'invisible [transform:translateY(100%)]',
        // Desktop: a sticky labelled column in the document flow. The un-hide
        // rule repeats the visibility so it does not lose to the base-tier one
        // and leave a 232 px empty track.
        'lg:visible lg:sticky lg:z-auto lg:rounded-none lg:border-0 lg:bg-transparent lg:p-0 lg:py-4 lg:pe-2 lg:shadow-none lg:[inset-block-start:5rem] lg:[inset-inline:auto] lg:[max-block-size:calc(100vh-7rem)] lg:[transform:none]',
      )}
    >
      <div className="mb-4 flex items-center gap-3 lg:hidden">
        <h2 className="font-display text-h4 font-bold">{chromeCopy.nav.heading}</h2>
        <button
          type="button"
          onClick={close}
          aria-label={chromeCopy.nav.close}
          className="hit-target ms-auto flex items-center justify-center rounded-sm hover:bg-hover-bg"
        >
          <CloseIcon />
        </button>
      </div>

      <nav aria-label={chromeCopy.nav.listLabel}>
        {groups.map((group) => (
          <div key={group.caption ?? 'root'}>
            {group.caption ? (
              // A section caption in the monospace face, deliberately outside
              // the heading hierarchy: it names a group, it is not a level.
              <p className="mono mb-1 mt-4 ps-3 text-xs text-ink-faint">{group.caption}</p>
            ) : null}

            <ul className="m-0 list-none p-0">
              {group.entries.map((entry) => {
                const state = navEntryState(entry.to, pathname);
                const Glyph = entry.icon;

                return (
                  <li key={entry.to} className="mt-2 first:mt-0">
                    <Link
                      to={entry.to}
                      aria-current={state === 'page' ? 'page' : undefined}
                      onClick={() => setOpen(false)}
                      className={cn(
                        'hit-target flex items-center gap-3 rounded-sm border-s-2 border-transparent px-3 py-2 text-sm no-underline',
                        state === 'idle'
                          ? 'text-ink-muted transition-colors duration-fast hover:bg-hover-bg hover:text-accent-text'
                          : 'border-s-accent bg-accent-soft font-semibold text-accent-text',
                      )}
                    >
                      <Glyph />
                      <span>{entry.label}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>
    </aside>
  );
}
